import { AsyncPipe } from '@angular/common';
import { Component, inject, OnDestroy, OnInit } from '@angular/core';
import {
  MAT_DIALOG_DATA,
  MatDialog,
  MatDialogConfig,
  MatDialogRef,
} from '@angular/material/dialog';
import { Actions, ofType } from '@ngrx/effects';
import { select, Store } from '@ngrx/store';
import { NGXLogger } from 'ngx-logger';
import { Observable, of, Subject, take, takeUntil } from 'rxjs';
import { AppExceptionModel } from '../../../../models/app-exception.model';
import { Grade } from '../../../../models/grade.school.model';
import { Group } from '../../../../models/group.model';
import { SchoolMember } from '../../../../models/member.school.model';
import { MembershipStatus } from '../../../../models/membership.status';
import * as memberActions from '../../../../state/actions/member.school.actions';
import { AppState } from '../../../../state/appstate';
import * as gradeSelector from '../../../../state/selectors/grade.school.selector';
import * as groupSelector from '../../../../state/selectors/group.selector';
import { DlgConfirmComponent } from '../../../components/dialogs/dlg-confirm/dlg-confirm.component';

@Component({
  selector: 'app-dlg-select-grade',
  standalone: true,
  imports: [AsyncPipe],
  templateUrl: './dlg-select-grade.component.html',
  styleUrl: './dlg-select-grade.component.css',
})
export class DlgSelectGradeComponent implements OnInit, OnDestroy {
  readonly dialogRef = inject(MatDialogRef<DlgSelectGradeComponent>);
  readonly data = inject<any>(MAT_DIALOG_DATA);
  private dialog: MatDialog = inject(MatDialog);

  private actions$: Actions = inject(Actions);
  logger: NGXLogger = inject(NGXLogger);

  store: Store<AppState> = inject(Store<AppState>);

  title: string = 'Assign Student Grade';
  description: string = 'Select a grade to proceed';
  action: string = 'Assign Grade';
  selectedOption: string = 'unassigned';

  student: SchoolMember = this.data.student;

  group: Group | undefined = undefined;

  grades$: Observable<Grade[]> = new Observable();

  working$: Observable<boolean> = of(false);
  exit$: Subject<void> = new Subject();

  constructor() {
    this.description = this.data.description;
    this.selectedOption = this.data.selectedOption ?? 'unassigned';
  }
  ngOnDestroy(): void {
    this.exit$.next();
    this.exit$.complete();
  }

  ngOnInit(): void {
    this.store
      .select(groupSelector.getGroupById(this.data.groupId))
      .pipe(take(1))
      .subscribe((group) => {
        this.group = group;
      });

    this.grades$ = this.store.pipe(
      select(gradeSelector.getGradesByGroup(this.data.groupId)),
    );

    this.actions$
      .pipe(ofType(memberActions.update), takeUntil(this.exit$))
      .subscribe(({ id, currMember, member }) => {
        this.logger.debug('Reassigning Grade...');
        this.logger.debug(`Id: ${id}`);
        this.logger.debug(
          `CurrentMember: ${JSON.stringify(currMember, null, 2)}`,
        );
        this.logger.debug(`Member: ${JSON.stringify(member, null, 2)}`);
        this.working$ = of(true);
      });

    // Subscribe to loadUserSuccess action
    this.actions$
      .pipe(ofType(memberActions.updateSuccess), takeUntil(this.exit$))
      .subscribe(() => {
        this.logger.debug('Grade reassigned successfully');
        this.working$ = of(false);
        this.dialogRef.close(true);
      });

    // Subscribe to updateFailure action (optional, for error handling)
    this.actions$
      .pipe(ofType(memberActions.updateFailure), takeUntil(this.exit$))
      .subscribe(({ error }) => {
        this.logger.debug('Grade reassignment failed');
        this.working$ = of(false);
      });
  }

  onChangeOption(event: any): void {
    this.selectedOption = event.target.value;
  }

  onCancel(): void {
    this.dialogRef.close();
  }

  submit(): void {
    this.grades$.pipe(take(1)).subscribe((grades) => {
      const grade: Grade | undefined = grades.find(
        (grade) => grade.id === this.selectedOption,
      );
      this.logger.debug(`Selected Grade: ${JSON.stringify(grade)}`);

      if (this.student == null || this.group == null) {
        this.onShowAlert({
          code: 'confirm-reassign-grade',
          title: 'Reassign Grade',
          message: `Student or Group information is missing. Unable to proceed.`,
        });
        return;
      }
      this.store.dispatch(
        memberActions.update({
          id: this.student?.id!,
          currMember: this.student,
          member: {
            groupId: this.student.groupId,
            memberId: this.student.memberId,
            memberType: this.student.memberType,
            gradeId: grade?.id ?? 'unassigned',
            gradeName: grade?.gradeName ?? 'Unassigned',
            gradeYear: this.group?.academicYear,
            status: MembershipStatus.active,
          } as SchoolMember,
        }),
      );
    });
  }

  onShowAlert(error: AppExceptionModel) {
    let dialogConfig = new MatDialogConfig();

    dialogConfig.data = {
      title: error.title,
      description: error.message,
      action: 'Okay',
      buttonColor: 'bg-primary-500',
      showType: 'error',
    };

    this.dialog.open(DlgConfirmComponent, dialogConfig);

    return;
  }
}
